import { useProfile } from "@/context/useProfileProps";
import { useRecruiterView } from "@/context/useRecruiterView";
import { emitCustomEvent } from "@/hooks/useCustomEvent";
import { DownloadAppCard } from "@monsterindia/seeker-jobs";
import { memo, useMemo, useRef, useState } from "react";

import CardLayout from "@/components/common/cardLayout";
import ProfileModal from "@/components/common/profileModal";
import { getPersonalDetails } from "@/components/profile/contactCard/query";
import { OTPRef } from "@/components/profile/contactCard/types";
import { getEducations } from "@/components/profile/educationCard/query";
import ExternalAd from "@/components/profile/externalAd/ExternalAd";
import { getPreference } from "@/components/profile/preferencesCard/query";
import { focusOnField } from "@/components/profile/profileScore/utils/constant";
import { actionModalMap } from "@/components/profile/profileScore/utils/modalMap";
import { getSkills } from "@/components/profile/skills/query";
import { getWorkExp } from "@/components/profile/workExperience/query";

import ActionItem from "./components/actionItem";
import ProfileScoreHeader from "./components/profileScoreHeader";
import Skeleton from "./components/skeleton";
import { getProfileStats } from "./utils/query";
import { PendingActionType, ProfileStatsApiType } from "./utils/types";

const ProfileScore = ({
  additionalClassName,
  type = "profile",
}: {
  additionalClassName?: string;
  type?: "profile" | "performance";
}) => {
  const { isRecruiterView } = useRecruiterView();
  const {
    translation: { profileScore: profileScoreContent },
    isMobile,
  } = useProfile();
  const formRef = useRef<HTMLFormElement>(null);
  const otpRef = useRef<OTPRef>(null);
  const { data, isLoading } = getProfileStats();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<string>("");
  const [pendingActionSelected, setPendingActionSelected] =
    useState<string>("");
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [formLevel, setFormLevel] = useState<number>(0); // to handle multiple form in some section opens another section like profileCard
  const [modalHeading, setModalHeading] = useState<string>("");
  const { data: edDetail } = getEducations();
  const { data: empDetail } = getWorkExp();
  const { data: personalDetailData } = getPersonalDetails();
  const { data: skillsDetails } = getSkills();
  const { data: preferenceData = null } = getPreference();

  const personalData =
    personalDetailData?.personalDetailSection?.personalDetails ?? null;
  const educationData =
    edDetail?.educationDetailSection?.educations?.[0] ?? null;
  const employmentData =
    empDetail?.employmentDetailSection?.employments?.[0] ?? null;
  const mobileNumberData =
    personalDetailData?.personalDetailSection?.personalDetails
      ?.primaryMobileNumber ?? null;
  const emailData =
    personalDetailData?.personalDetailSection?.personalDetails?.primaryEmail ??
    null;
  const skillsData = skillsDetails?.skillSection?.skills || [];

  const { profileScore, pendingActions } = (data ?? {
    profileScore: 0,
    pendingActions: [],
  }) as ProfileStatsApiType;

  const handleModalAction = (popup: string, action: string) => {
    setPendingActionSelected(action);
    if (popup === "resume") {
      // const event = new CustomEvent("UPLOAD_RESUME");
      // window.dispatchEvent(event);
      emitCustomEvent("UPLOAD_RESUME");
    } else {
      setModalAction(popup);
      setShowModal(true);
    }
  };

  const webLayout = (
    <CardLayout
      containerClassName={`${additionalClassName} ${type === "performance" && pendingActions?.length > 3 ? "max-h-[250px] overflow-y-auto no-scrollbar rounded-xl bg-white" : ""}`}
      className={
        type === "performance"
          ? "rounded-xl border border-[rgba(232,235,240,0.5)] bg-white shadow-[0_10px_45px_0_rgba(0,0,0,0.03)]"
          : ""
      }
    >
      <div className="flex flex-col gap-6">
        <ProfileScoreHeader
          title="Profile score"
          description={
            typeof profileScore !== "undefined" && profileScore < 100
              ? profileScoreContent?.profileScoreLessThan100
              : profileScoreContent?.profileScore100
          }
          score={profileScore}
          viewType={showModal ? "modal" : "web"}
        />
        {typeof profileScore !== "undefined" && profileScore < 100 ? (
          <div
            className={`flex w-full flex-col rounded-xl ${showModal && isMobile ? "border-grey-05 gap-6 border p-4" : ""}`}
          >
            <div className="text-content-primary text-sm font-bold leading-5">
              {profileScoreContent?.pendingAction}
            </div>
            <div className="mt-3 flex w-full flex-col gap-6">
              {pendingActions &&
                pendingActions.map((item: PendingActionType, index: number) => (
                  <ActionItem
                    key={index}
                    {...item}
                    handleModalAction={handleModalAction}
                  />
                ))}
            </div>
          </div>
        ) : null}
      </div>
    </CardLayout>
  );

  const mSiteLayout = !showModal && profileScore < 100 && (
    <div
      className="fixed bottom-16 right-3 z-10"
      onClick={() => setShowModal(true)}
    >
      <ProfileScoreHeader
        title="Profile score"
        description="Recruiters seek 100% profiles - complete yours to stand out!"
        score={profileScore || 0}
        viewType="msite"
      />
    </div>
  );

  const handleModalActionBackBtn = () => {
    // back button is available for mobile. In desktop only if formLevel > 0
    if (formLevel > 0) {
      // to handle multiple form in some section opens another section like profileCard
      setFormLevel(0);
    } else {
      // if on first level then render pending actions , this is only for mobile
      setModalAction("");
      setModalHeading("");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setModalAction("");
    setModalHeading("");
    setFormLevel(0);
  };

  const handleFormLevel = (level: number) => {
    if (level > 0) {
      setModalHeading(level === 1 ? "Education" : "Work Experience");
    }
    setFormLevel(level);
  };

  const handleFooterBtnLoading = (data: { isSaveLoading: boolean }) => {
    const { isSaveLoading } = data;
    setIsSaveLoading(isSaveLoading);
  };

  const renderActionModal = useMemo(() => {
    if (actionModalMap[modalAction]?.label && formLevel === 0) {
      setModalHeading(actionModalMap[modalAction].label);
    }

    const Component = actionModalMap[modalAction]?.component;
    if (!Component) {
      return null;
    }

    const getPrefilledData = () => {
      switch (modalAction) {
        case "preference":
          return preferenceData;
        case "skills":
          return skillsData;
        case "profile-card":
          return personalData;
        case "work":
          return { employmentPrefill: employmentData };
        default:
          return {};
      }
    };

    return (
      <Component
        isEdit={true}
        isCalledFromOutside={true}
        closeEdit={isMobile ? handleModalActionBackBtn : handleModalClose} // if pending action section is opened then perform back action, else close modal
        formRef={formRef}
        otpRef={otpRef}
        handleFooterBtnLoading={handleFooterBtnLoading} // to handle loading state of save button
        prefilledData={getPrefilledData()} // prefills data for profile card
        educationData={educationData} // for profile card
        employmentData={employmentData} // for profile card
        formLevel={formLevel}
        handleFormLevel={handleFormLevel}
        email={emailData?.email ?? null} // for contact card
        mobileNumber={mobileNumberData?.mobileNumber ?? null} // contact card
        countryCode={mobileNumberData?.countryCode ?? null} // contact card
        type={"CHANGE_MOBILE_MOBILE"}
        focusOnField={focusOnField[pendingActionSelected] ?? ""}
      />
    );
  }, [modalAction, formLevel]);

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
    if (otpRef.current) {
      otpRef.current.verifyOTPHandler();
    }
  };

  const getModalSaveBtnlabel = () => {
    switch (modalAction) {
      case "mobile":
        return "Save & Verify";
      case "email":
        return "Confirm";
      default:
        return "Save";
    }
  };

  // Here modalAction is used to render modal of selected pending action
  const modalLayout = showModal && (
    <ProfileModal
      showModal={showModal}
      closeModal={handleModalClose}
      isMobile={isMobile}
      showCloseButton={true}
      screenAlignment={isMobile ? "bottom-sheet" : "center"}
      showHandleBar={true}
      heading={modalHeading ? modalHeading : "Pending Actions"}
      footerPrimaryBtnLabel={modalAction ? getModalSaveBtnlabel() : ""}
      footerPrimaryBtnHandler={handleFormSubmit}
      footerPrimaryBtnIsLoading={isSaveLoading}
      customClass={isMobile ? "min-h-[62vh]" : formLevel > 0 ? "h-[70vh]" : ""} // formLevel > 0 means education or work experience form is opened from profile card
      backBtnhandler={
        (modalAction && isMobile) || formLevel > 0
          ? handleModalActionBackBtn
          : null
      }
    >
      {modalAction ? renderActionModal : webLayout}
    </ProfileModal>
  );

  if (isLoading) {
    return (
      <CardLayout>
        <Skeleton />
      </CardLayout>
    );
  }

  if (isRecruiterView) {
    return null;
  }

  return (
    <>
      {/* if mobile show profile score as floating button but in desktop as a section */}
      {isMobile && type === "profile" ? mSiteLayout : webLayout}
      {isMobile ? null : (
        <div className={`mt-6 flex flex-col items-center`}>
          <DownloadAppCard />
          <ExternalAd
            adName="BB-Desktop-Profile-Score"
            additionalClass={`mt-6 ${additionalClassName}`}
            adSize={[300, 250]}
            isBBSlot={true}
            isLoggedIn={true}
            adPageName="profilePage"
          />
        </div>
      )}

      {/* if mobile show pending actions in modal and also section to open as per pending action */}
      {modalLayout}
    </>
  );
};

export default memo(ProfileScore);
